import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  extra_days: number;
  reason: string;
};

interface Props {
  onClose: () => void;
}

const FreelancerExtraDaysModal: FC<Props> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const { id } = useParams();

  const methods = useForm<FormDataT>({
    defaultValues: {},
  });

  const {
    control,
    handleSubmit,
    clearErrors,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post<never, { id: number }>(
          `/v1/payments/freelancers/holidays/balance/${id}/add-extra-days`,
          data,
        );

        await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
          exact: false,
        });

        await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
          exact: false,
        });

        alerts.success(`Vacation request created successfully`);
        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, false, true);
      }
    })(e);
  };

  const reasonsOptions = [
    { label: 'For Kids', value: 'For Kids' },
    { label: 'Extra Case', value: 'Extra Case' },
  ];

  return (
    <SidePanel heading="Add extra days" onClose={onClose}>
      <SidePanelBody onSubmit={onSubmit} autoComplete="off">
        <SidePanelContent>
          <FormLayout>
            <FormField<FormDataT>
              control={control}
              name="extra_days"
              type="text"
              label="Days Count"
              placeholder="Add days"
            />
            <FormField<FormDataT>
              control={control}
              name="reason"
              type="select"
              options={reasonsOptions}
              label="Reason"
            />
          </FormLayout>
        </SidePanelContent>
        <SidePanelFooter $actions>
          <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
            Submit
          </Button>
          <Button color="secondary" onClick={onClose} type="button">
            Close
          </Button>
        </SidePanelFooter>
      </SidePanelBody>
    </SidePanel>
  );
};

export default FreelancerExtraDaysModal;
